import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Container,
    FormFeedback,
    Form,
    Button,
    ButtonGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import ResultHandler from "../../../components/ResultHandler";
import FormButtonHandler from "../../../components/FormButtonHandler";
import { useResponse } from "../../../hooks/useResponse";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import useCustomValidation from "../validationSchema";
import { addStatusLeadContact, getStatusLeadContacts, editStatusLeadContact } from "../../../api/statusLeadContact";

const LEVEL_OPTIONS = [
    { value: 'start', label: 'Start', color: 'primary' },
    { value: 'failed', label: 'Failed', color: 'danger' },
    { value: 'final', label: 'Final', color: 'success' },
    { value: null, label: 'Default', color: 'secondary' }
];

const AddStatusLeadContact = () => {
    const { t } = useTranslation();
    const { addStatusLeadContactSchema } = useCustomValidation();
    const navigate = useNavigate();

    const [usedLevels, setUsedLevels] = useState([]);
    const [levelStatuses, setLevelStatuses] = useState({});
    const [statusesById, setStatusesById] = useState({});
    const [modal, setModal] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);

    useEffect(() => {
        const fetchLevels = async () => {
            try {
                const res = await getStatusLeadContacts({}, { page: 1, limit: 100 });
                if (res?.data) {
                    const levels = res.data.map(item => item.level);
                    const levelMap = {};
                    const idMap = {};

                    res.data.forEach(item => {
                        if (item.level) {
                            levelMap[item.level] = { name: item.name, id: item._id };
                        }
                        idMap[item._id] = item;
                    });

                    setUsedLevels(levels);
                    setLevelStatuses(levelMap);
                    setStatusesById(idMap);
                }
            } catch (err) {
                console.error("Fetch level error", err);
            }
        };

        fetchLevels();
    }, []);

    const {
        setData,
        loading,
        error,
        success,
    } = useResponse(addStatusLeadContact, () => {
        navigate("/statusLeadContacts/");
    });

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: "",
            level: null
        },
        validationSchema: Yup.object().shape({
            name: Yup.string().required(t("Name is required")),
            level: Yup.mixed().nullable().oneOf(['start', 'failed', 'final', null], t("Invalid level selected"))
        }),
        onSubmit: (values) => {
            if (values.level && usedLevels.includes(values.level) && !modal) {
                setSelectedLevel(values.level);
                setModal(true);
            } else {
                setData({ data: values, type: "add" });
            }
        },
    });

    const isDuplicateNameError = error?.error === "DUPLICATE_NAME";

    const handleLevelSelect = (level) => {
        validation.setFieldValue("level", level);
    };

    const toggle = () => {
        setModal(!modal);
        setSelectedLevel(null);
    };

    const handleOverride = async () => {
        try {
            const current = levelStatuses[selectedLevel];
            if (current?.id) {
                await editStatusLeadContact(current.id, {
                    name: statusesById[current.id].name,
                    level: null
                });
            }

            setData({ data: validation.values, type: "add" });

            const updated = { ...levelStatuses };
            delete updated[selectedLevel];
            setLevelStatuses(updated);

            setModal(false);
        } catch (err) {
            console.error("Override error:", err);
            alert(t("Failed to override level. Please try again."));
        }
    };

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs title={`${t("btns.Add")} ${t("Status Lead Contact")}`} />

                <Form
                    className="needs-validation"
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                    }}
                >
                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Name")}</Label>
                                <Input
                                    name="name"
                                    placeholder={t("Name")}
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.name}
                                    invalid={
                                        !!(
                                            (validation.touched.name && validation.errors.name) ||
                                            isDuplicateNameError
                                        )
                                    }
                                />
                                <FormFeedback>
                                    {validation.errors.name ||
                                        (isDuplicateNameError && t("Name already exists"))}
                                </FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Level")}</Label>
                                <ButtonGroup className="w-100 gap-2">
                                    {LEVEL_OPTIONS.map((option) => {
                                        const isSelected = validation.values.level === option.value;
                                        const currentStatus = levelStatuses[option.value];
                                        return (
                                            <Button
                                                key={option.value}
                                                color={isSelected ? option.color : 'light'}
                                                outline={!isSelected}
                                                onClick={() => handleLevelSelect(option.value)}
                                                className={`flex-grow-1 text-capitalize position-relative d-flex align-items-center justify-content-center gap-2 ${isSelected ? 'shadow-sm' : ''}`}
                                                style={{
                                                    minWidth: '120px',
                                                    border: isSelected ? `2px solid var(--bs-${option.color})` : '1px solid #dee2e6',
                                                    transition: 'all 0.2s ease',
                                                    height: '50px'
                                                }}
                                            >
                                                <span>{t(option.label)}</span>
                                            </Button>
                                        );
                                    })}
                                </ButtonGroup>
                                {validation.touched.level && validation.errors.level && (
                                    <div className="text-danger mt-1">{validation.errors.level}</div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormButtonHandler btnTxt={t("Add")} loading={loading} />
                    <ResultHandler
                        error={isDuplicateNameError ? null : error}
                        success={success}
                        successMsg={t("Created Successfully!")}
                    />
                </Form>

                <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle}>{t("Level Already in Use")}</ModalHeader>
                    <ModalBody>
                        {selectedLevel && levelStatuses[selectedLevel] && (
                            <p>
                                {t("This level is already taken by")} <strong>{levelStatuses[selectedLevel].name}</strong>.
                                {t("Do you want to override it? The level will be removed from the existing status.")}
                            </p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggle}>{t("Cancel")}</Button>
                        <Button color="primary" onClick={handleOverride}>{t("Override")}</Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default AddStatusLeadContact;
